import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { Plus, Trash2, Check, X, Quote } from "lucide-react";

const BLANK = { name: "", place: "", quote: "", approved: true };

export default function AdminTestimonials() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/testimonials");
    setItems(data.testimonials);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (form.name.trim().length < 2 || form.quote.trim().length < 5) { toast.error("Add a name and a short quote."); return; }
    setSaving(true);
    try { await api.post("/admin/testimonials", form); setForm(BLANK); toast.success("Testimonial added"); load(); }
    catch { toast.error("Failed"); } finally { setSaving(false); }
  };
  const toggle = async (it) => { try { await api.put(`/admin/testimonials/${it.id}`, { ...it, approved: !it.approved }); load(); } catch { toast.error("Failed"); } };
  const del = async (it) => { if (!window.confirm(`Delete testimonial from ${it.name}?`)) return; try { await api.delete(`/admin/testimonials/${it.id}`); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-5 max-w-2xl">
      <h1 className="text-2xl md:text-3xl font-heading font-extrabold">Testimonials</h1>
      <p className="text-sm text-thali-ink/60">Up to 3 approved testimonials rotate on the ₹99 paywall screen. Only real, approved quotes are shown.</p>

      <div className="card-thali space-y-3" data-testid="testimonial-form">
        <div className="font-heading font-bold flex items-center gap-2"><Plus className="w-5 h-5" /> Add testimonial</div>
        <div className="grid grid-cols-2 gap-2">
          <input className="input-thali" placeholder="Teacher name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="testimonial-name" />
          <input className="input-thali" placeholder="School / Block (optional)" value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} data-testid="testimonial-place" />
        </div>
        <textarea className="input-thali min-h-[80px]" placeholder="Short quote…" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} data-testid="testimonial-quote" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.approved} onChange={(e) => setForm({ ...form, approved: e.target.checked })} className="w-4 h-4 accent-thali-green" /> Approved (show on paywall)</label>
        <button onClick={add} disabled={saving} className="btn-3d-green w-full" data-testid="testimonial-add"><Plus className="w-4 h-4" /> {saving ? "…" : "Add"}</button>
      </div>

      {!items ? <Loader /> : items.length === 0 ? (
        <div className="text-center py-8 text-thali-ink/50">No testimonials yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="card-thali" data-testid={`testimonial-${it.id}`}>
              <div className="flex items-start gap-2">
                <Quote className="w-5 h-5 text-thali-turmericdark shrink-0" />
                <div className="flex-1">
                  <div className="text-sm text-thali-ink/80">“{it.quote}”</div>
                  <div className="text-xs text-thali-ink/50 mt-1 font-semibold">— {it.name}{it.place ? `, ${it.place}` : ""}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${it.approved ? "bg-green-100 text-green-700" : "bg-thali-ink/10 text-thali-ink/60"}`}>{it.approved ? "Approved" : "Hidden"}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => toggle(it)} className="btn-ghost text-xs px-3 min-h-[40px]" data-testid={`testimonial-toggle-${it.id}`}>{it.approved ? <><X className="w-4 h-4" /> Hide</> : <><Check className="w-4 h-4" /> Approve</>}</button>
                <button onClick={() => del(it)} className="btn-ghost text-xs px-3 min-h-[40px] text-red-600 border-red-200" data-testid={`testimonial-delete-${it.id}`}><Trash2 className="w-4 h-4" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
